import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconLogout, IconUser } from '@tabler/icons-react';
import axios from '../../library/axios';
import {
  CPANEL_USER_UPDATED_EVENT,
  formatCentralRoleLabel,
  getCentralUser,
  setCentralUser,
} from '../../helpers/cpanelAuth';
import CpanelNotifications from './CpanelNotifications';
import CentralUserAvatar from './CentralUserAvatar';

const CpanelHeader = ({ onLogout }) => {
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(() => getCentralUser());
  const displayName = user?.namaLengkap || user?.username || 'Administrator';
  const username = user?.username || 'admin';
  const roleLabel = formatCentralRoleLabel(user?.role) || 'Operator';

  useEffect(() => {
    let cancelled = false;

    axios
      .get('/cpanel/auth/me')
      .then(({ data }) => {
        if (cancelled || !data?.user) return;
        const merged = { ...getCentralUser(), ...data.user };
        setCentralUser(merged);
        setUser(merged);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleUserUpdated = (event) => {
      const next = event?.detail || getCentralUser();
      if (next) setUser(next);
    };

    window.addEventListener(CPANEL_USER_UPDATED_EVENT, handleUserUpdated);
    return () => window.removeEventListener(CPANEL_USER_UPDATED_EVENT, handleUserUpdated);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleLogoutClick = (event) => {
    event.preventDefault();
    setOpen(false);
    onLogout?.();
  };

  return (
    <div className="d-flex align-items-center justify-content-end flex-lg-grow-1 gap-2 cpanel-header-actions">
      <CpanelNotifications />
      <div
        ref={menuRef}
        className={`cpanel-header-user${open ? ' is-open' : ''}`}
        id="kt_header_user_menu_toggle"
      >
        <button
          type="button"
          className="cpanel-header-user__toggle"
          onClick={handleToggle}
          aria-expanded={open}
          aria-haspopup="true"
          aria-label="Menu profil"
        >
          <CentralUserAvatar user={user} displayName={displayName} size="sm" />
          <span className="cpanel-header-user__name d-none d-sm-inline">{displayName}</span>
          <span className="cpanel-header-user__chevron" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 24 24" fill="none">
              <path
                d="M6 9L12 15L18 9"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>

        {open ? (
          <div className="central-user-menu cpanel-header-user__menu" role="menu">
            <div className="central-user-menu__header">
              <CentralUserAvatar user={user} displayName={displayName} size="lg" />
              <div className="central-user-menu__info">
                <span className="central-user-menu__name">{displayName}</span>
                <span className="central-user-menu__username">@{username}</span>
                <span className="central-user-menu__badge">{roleLabel}</span>
              </div>
            </div>

            <div className="central-user-menu__body">
              <Link
                to="/cpanel/profile"
                className="central-user-menu__logout mb-2"
                onClick={() => setOpen(false)}
              >
                <span className="central-user-menu__logout-icon">
                  <IconUser size={18} stroke={1.75} />
                </span>
                <span>Profil</span>
              </Link>
              <button type="button" className="central-user-menu__logout" onClick={handleLogoutClick}>
                <span className="central-user-menu__logout-icon">
                  <IconLogout size={18} stroke={1.75} />
                </span>
                <span>Keluar</span>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CpanelHeader;
